import React, {
  useCallback, useContext, useEffect, useReducer,
} from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import {
  Button,
  FormControlLabel,
  FormGroup,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { AccountCircle, InputRounded } from '@mui/icons-material';
import { Title, TooltipIcon } from 'shared/components/abstract/interactables';
import { SideNavContext } from 'shared/hoc/withSideNav';
import { getUnitPath } from 'shared/routes/building';
import { formatPhysicalAddress, formatTenantName, formatUnitName } from 'shared/utils/formatters';
import { getBuilding, searchBuildings } from 'shared/api/buildings';
import { getUnit, searchBuildingUnits } from 'shared/api/units';
import { createOnboardAccount, searchOnboardAccount } from 'features/tenant/api/onboard';
import { createTenant, createTenantUnitReference, searchTenants } from 'shared/api/tenants';
import { useAuth, useNotification, useRequest } from 'shared/hooks';
import { AsyncSearchableSelect } from 'shared/components/select/SearchableSelects';
import { TextEditor } from 'shared/components/data-display/editors';
import { SectionTitle } from 'shared/components/data-display/GridFormatter';
import FormCard from 'shared/components/FormCard';
import { formatPhoneNumber } from 'shared/utils';
import { VARIANTS } from 'shared/constants';
import StickyHeadTable from 'shared/components/table/StickyHeadTable';
import { getTenantCreatePath, getTenantMovePath, getTenantPath } from 'shared/routes/tenant';
import { getUnitOptionLabel, validateTenantData } from './helper';
import { cycleGroupDpsk } from './tenantServices';

// Functions that return query strings for the tenant search API.
// These are run before create to check if the data already exists.
function nameSearch(fullname) {
  return `?include_archived=true&fullname=${fullname}`;
}

function phoneSearch(phone) {
  return `?include_archived=true&q=${phone}`;
}

function emailSearch(email) {
  return `?include_archived=true&email=${email}`;
}

const initialState = {
  altPhone: '',
  building: null,
  createCount: 0,
  dialogOpen: false,
  duplicateTenants: [],
  email: '',
  firstname: '',
  lastname: '',
  newOnboardAccount: true,
  note: '',
  phone: '',
  unit: null,
};

function reducer(state, {
  action,
  payload,
}) {
  switch (action) {
    case 'building':
      return {
        ...state,
        building: payload,
        unit: null,
      };
    case 'unit':
      return {
        ...state,
        unit: payload,
      };
    case 'dialog':
      return {
        ...state,
        dialogOpen: payload,
      };
    case 'tenantData':
      if (!state.dialogOpen) {
        return {
          ...state,
          duplicateTenants: [],
          [payload.name]: payload.value,
        };
      }
      return {
        ...state,
      };
    case 'duplicates':
      return {
        ...state,
        dialogOpen: true,
        duplicateTenants: payload,
      };
    case 'create':
      return {
        ...initialState,
        building: state.building,
        createCount: state.createCount + 1,
        newOnboardAccount: state.newOnboardAccount,
        unit: {
          ...state.unit,
          tenant_count: state.unit.tenant_count + 1,
        },
      };
    default:
      return state;
  }
}

export default function TenantCreator() {
  const syncSideNav = useContext(SideNavContext);
  const { search } = useLocation();
  const {
    request,
    isLoading,
  } = useRequest();
  const { pushInfo } = useNotification();

  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    altPhone,
    building,
    createCount,
    dialogOpen,
    duplicateTenants,
    email,
    firstname,
    lastname,
    newOnboardAccount,
    note,
    phone,
    unit,
  } = state;

  const { push: pushHistory } = useHistory();
  const { user: { username } } = useAuth();
  const notification = useNotification();
  const hasDuplicates = duplicateTenants?.length > 0;
  const hasDuplicateOnboardAccount = hasDuplicates
    && duplicateTenants.some((duplicateTenant) => duplicateTenant.onboard_account?.id > 0);
  const fullname = `${firstname} ${lastname}`;

  const searchParams = new URLSearchParams(search);
  const unitId = parseInt(searchParams.get('unit_id'), 10);
  const buildingId = parseInt(searchParams.get('building_id'), 10);

  // Sync building to ?building_id url param.
  useEffect(() => {
    (async () => {
      if (buildingId && (!building || building.id !== buildingId)) {
        const result = await request(getBuilding(buildingId));
        if (result) {
          dispatch({
            action: 'building',
            payload: result,
          });
        }
      } else if (!buildingId) {
        dispatch({
          action: 'building',
          payload: null,
        });
      }
    })();
  }, [request, buildingId, building]);

  // Sync unit to ?unit url param.
  useEffect(() => {
    (async () => {
      if (unitId && (!unit || unit.id !== unitId)) {
        const result = await request(getUnit(unitId));
        if (result) {
          dispatch({
            action: 'unit',
            payload: result,
          });
        }
      } else if (!unitId) {
        dispatch({
          action: 'unit',
          payload: null,
        });
      }
    })();
  }, [request, unitId, unit]);

  const onTenantChange = (name) => (value) => {
    dispatch({
      action: 'tenantData',
      payload: {
        name,
        value,
      },
    });
  };

  const onBuildingChange = useCallback((b) => {
    dispatch({
      action: 'building',
      payload: b,
    });
    pushHistory(getTenantCreatePath(b && b.id));
  }, [pushHistory]);

  const onUnitChange = useCallback((u) => {
    dispatch({
      action: 'unit',
      payload: u,
    });
    pushHistory(getTenantCreatePath(building && building.id, u && u.id));
  }, [pushHistory, building]);

  const checkDuplicates = async () => {
    const [
      fullnameResult,
      phoneResult,
      emailResult,
      onboardResult,
    ] = await Promise.all([
      firstname && lastname ? request(searchTenants(nameSearch(fullname))) : Promise.resolve(null),
      phone.length > 9 ? request(searchTenants(phoneSearch(phone))) : Promise.resolve(null),
      email.includes('@') ? request(searchTenants(emailSearch(email))) : Promise.resolve(null),
      newOnboardAccount ? request(searchOnboardAccount(email, phone)) : Promise.resolve(null),
    ]);

    const result = {};
    if (fullnameResult && fullnameResult.total > 0) {
      result.duplicateTenants = fullnameResult.rows;
    }
    if (phoneResult && phoneResult.total > 0) {
      if (result.duplicateTenants?.length > 0) {
        result.duplicateTenants = result.duplicateTenants.concat(phoneResult.rows.filter(
          (tenant) => !result.duplicateTenants.some(
            (proposedTenant) => proposedTenant.id === tenant.id,
          ),
        ));
      } else {
        result.duplicateTenants = phoneResult.rows;
      }
    }
    if (emailResult && emailResult.total > 0) {
      if (result.duplicateTenants?.length > 0) {
        result.duplicateTenants = result.duplicateTenants.concat(emailResult.rows.filter(
          (tenant) => !result.duplicateTenants.some(
            (proposedTenant) => proposedTenant.id === tenant.id,
          ),
        ));
      } else {
        result.duplicateTenants = emailResult.rows;
      }
    }
    if (onboardResult && onboardResult.total > 0) {
      if (result.duplicateTenants?.length > 0) {
        onboardResult.rows.filter(
          (onboardAccount) => result.duplicateTenants.some(
            (proposedTenant, index) => (
              proposedTenant.id === onboardAccount.tenant_id
                ? result.duplicateTenants.splice(
                  index,
                  1,
                  {
                    ...proposedTenant,
                    onboard_account: onboardAccount,
                  },
                )
                : false),
          ),
        );
      } else {
        result.duplicateTenants = onboardResult.rows;
      }
    }
    return result.duplicateTenants;
  };

  const createTenantHandler = async (tenant) => {
    const newTenant = await request(createTenant(tenant));
    if (!newTenant) {
      dispatch({
        action: 'dialog',
        payload: false,
      });
      return;
    }
    const tenantId = newTenant.id;

    syncSideNav(getTenantPath(tenantId), formatTenantName(newTenant));

    let success = await cycleGroupDpsk(request, building, unit);
    if (!success) {
      dispatch({
        action: 'dialog',
        payload: false,
      });
      return;
    }

    success = await request(createTenantUnitReference(tenantId, unitId, username));
    if (!success) {
      dispatch({
        action: 'dialog',
        payload: false,
      });
      return;
    }

    dispatch({ action: 'create' });
    if (newOnboardAccount) {
      const onboardResult = await request(
        createOnboardAccount(tenantId),
        'Successfully created Onboard Account',
        null,
        'Failed to create Onboard Account',
      );
      if (!onboardResult) {
        dispatch({
          action: 'dialog',
          payload: false,
        });
        return;
      }
    }

    dispatch({
      action: 'dialog',
      payload: false,
    });
    notification.pushSuccess('Tenant created');
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!unit) {
      pushInfo('Please select a unit.');
      return;
    }

    const tenant = {
      alt_phone: altPhone,
      email,
      first_name: firstname,
      last_name: lastname,
      note,
      phone,
    };

    const message = validateTenantData(tenant, newOnboardAccount);
    if (message) {
      notification.pushError(message);
      return;
    }

    const duplicates = await checkDuplicates();
    if (duplicates?.length > 0) {
      dispatch({
        action: 'duplicates',
        payload: duplicates,
      });
      return;
    }

    createTenantHandler(tenant);
  };

  let unitLinkText;
  if (unit && building) {
    unitLinkText = `${formatPhysicalAddress(building, formatUnitName(unit.name))}`;
  }

  const searchUnits = useCallback((searchString) => searchBuildingUnits(searchString, '', buildingId), [buildingId]);

  function userLink(id, name) {
    return <Link to={getTenantPath(id)}>{name}</Link>;
  }

  function moveTenantButton(id) {
    return (
      <Tooltip title="Move tenant">
        <IconButton
          color="primary"
          href={getTenantMovePath(buildingId, unitId, id)}
          target="_blank"
        >
          <InputRounded />
        </IconButton>
      </Tooltip>
    );
  }

  const tenantColumns = [
    {
      name: 'Tenant Name',
      formatter: (row) => userLink(row.id, `${row.first_name} ${row.last_name}`),
    },
    {
      name: 'Email',
      jsonKey: 'email',
    },
    {
      name: 'Phone',
      formatter: (row) => formatPhoneNumber(row.phone),
    },
    {
      name: 'Address',
      formatter: (row) => (
        row.building_id
          ? (
            <Link to={getUnitPath(row.building_id, row.unit_id)}>
              {formatPhysicalAddress(row, formatUnitName(row.unit_name))}
            </Link>
          )
          : ''
      ),
    },
    {
      name: '',
      jsonKey: 'moveTenant',
      formatter: (row) => moveTenantButton(row.id),
    },
  ];

  const onboardAccountColumn = {
    name: 'Onboard Account',
    formatter: (row) => (
      row.onboard_account !== null
      && (
        <TooltipIcon
          icon={AccountCircle}
          message="A duplicate Onboard Account exists for registered contact info"
          color="error"
        />
      )
    ),
  };

  const tenant = {
    alt_phone: altPhone,
    email,
    first_name: firstname,
    last_name: lastname,
    note,
    phone,
  };

  return (
    <form onSubmit={onSubmit}>
      <Title title="Create Tenants" />
      <FormCard>
        <Grid
          container
          rowSpacing={1}
          columnSpacing={{ xs: 1, sm: 2, md: 3 }}
        >
          <Grid size={12}>
            <SectionTitle>Location</SectionTitle>
          </Grid>
          <Grid size={{ lg: 6, xs: 12 }}>
            <AsyncSearchableSelect
              defaultOptions={false}
              onChange={onBuildingChange}
              label="Select Building"
              searchRequest={searchBuildings}
              selectedOption={building}
            />
          </Grid>
          <Grid size={{ lg: 6, xs: 12 }}>
            <AsyncSearchableSelect
              disabled={!buildingId}
              getOptionLabel={getUnitOptionLabel}
              onChange={onUnitChange}
              label="Select Unit"
              searchRequest={searchUnits}
              selectedOption={unit}
              updateCount={createCount}
            />
          </Grid>
          <Grid
            container
            size={12}
            sx={{
              visibility: unitLinkText ? 'visible' : 'hidden',
            }}
          >
            <Grid size={12}>
              <Typography>
                <strong>
                  {'Creating a tenant in: '}
                  <Link to={getUnitPath(buildingId, unitId)}>{unitLinkText}</Link>
                </strong>
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography>
                {'Or '}
                <Link to={getTenantMovePath(buildingId, unitId)}>
                  move
                </Link>
                {' an existing tenant here.'}
              </Typography>
            </Grid>
          </Grid>
          <Grid size={12}>
            <SectionTitle>Tenant</SectionTitle>
          </Grid>
          <Grid
            container
            sx={{
              justifyContent: 'end',
            }}
          >
            <Grid size={{ lg: 6, xs: 12 }}>
              <TextEditor
                label="First Name"
                value={firstname}
                setValue={onTenantChange('firstname')}
              />
            </Grid>
            <Grid size={{ lg: 6, xs: 12 }}>
              <TextEditor label="Last Name" value={lastname} setValue={onTenantChange('lastname')} />
            </Grid>
            <Grid size={{ lg: 6, xs: 12 }}>
              <TextEditor label="Phone" value={phone} setValue={onTenantChange('phone')} />
            </Grid>
            <Grid size={{ lg: 6, xs: 12 }}>
              <TextEditor label="Alt Phone" value={altPhone} setValue={onTenantChange('altPhone')} />
            </Grid>
            <Grid size={{ lg: 6, xs: 12 }}>
              <TextEditor label="Email" value={email} setValue={onTenantChange('email')} />
            </Grid>
            <Grid size={{ lg: 6, xs: 12 }}>
              <FormGroup>
                <FormControlLabel
                  sx={{ width: 'fit-content' }}
                  control={(
                    <Checkbox
                      checked={newOnboardAccount}
                      onChange={() => dispatch({
                        action: 'tenantData',
                        payload: {
                          name: 'newOnboardAccount',
                          value: !newOnboardAccount,
                        },
                      })}
                    />
                )}
                  label="Create Onboard Account?"
                />
              </FormGroup>
            </Grid>
            <Grid>
              <Button
                variant="contained"
                color={VARIANTS.primary}
                type="submit"
                disabled={isLoading || !building || !unit}
              >
                Create
              </Button>
            </Grid>
            <Dialog
              open={dialogOpen}
              size="lg"
              fullWidth
              maxWidth="lg"
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">
                Confirm Tenant Creation
              </DialogTitle>
              <DialogContent>
                <DialogContentText component="span" id="alert-dialog-description">
                  {`The following ${duplicateTenants.length > 1 ? `${duplicateTenants.length} duplicate tenants were found:` : 'duplicate tenant was found:'}`}
                </DialogContentText>
                <StickyHeadTable
                  columns={newOnboardAccount
                    ? tenantColumns.toSpliced(4, 0, onboardAccountColumn)
                    : tenantColumns}
                  noRowsText=""
                  rowId="id"
                  rows={duplicateTenants}
                  striped
                />
                {newOnboardAccount && hasDuplicateOnboardAccount && (
                <Grid sx={{
                  paddingY: 2,
                }}
                >
                  <Typography
                    variant="body1"
                    color="error"
                    sx={{
                      fontWeight: 'bold',
                    }}
                  >
                    You have selected to create a new Onboard Account while there is a duplicate
                    account already registered to the supplied contact info.
                  </Typography>
                  <Typography variant="body1" color="error">
                    Either move the duplicate account for continued use, supply new and unique
                    contact info, or delete the previous account.
                  </Typography>
                </Grid>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  variant="outlined"
                  onClick={() => dispatch({
                    action: 'dialog',
                    payload: false,
                  })}
                >
                  Close
                </Button>
                <Button
                  color={VARIANTS.warning}
                  disabled={newOnboardAccount && hasDuplicateOnboardAccount}
                  variant="contained"
                  onClick={() => createTenantHandler(tenant)}
                  autoFocus
                >
                  {hasDuplicates ? 'Create Duplicate Tenant' : 'Create'}
                </Button>
              </DialogActions>
            </Dialog>

          </Grid>
        </Grid>
      </FormCard>
    </form>
  );
}
